import React from "react";
import LegalShell, { LegalSection, LegalList } from "@/components/LegalShell";

/**
 * Terms of Service — Mythicmatrix.
 *
 * NOTE: Starter template drafted to cover subscriptions, user-generated worlds,
 * AI content caveats, and the permadeath promise. NOT legal advice. Have an
 * attorney tailor Section 11 (Governing Law) to your jurisdiction, and
 * Section 7 (Refunds) to your billing stance before launch.
 */
export default function Terms() {
  return (
    <LegalShell title="Terms of Service" lastUpdated="February 8, 2026" testid="terms-page">
      <p>
        These Terms govern your use of Mythicmatrix ("the Vault", "we", "us").
        By creating an account or using the service you agree to them.
      </p>

      <LegalSection title="1. Eligibility">
        <p>
          You must be at least 13 years old to use Mythicmatrix. If you are
          under 18 you must have your parent or guardian's permission.
          Mythicmatrix is intended for personal, non-commercial entertainment.
        </p>
      </LegalSection>

      <LegalSection title="2. Accounts">
        <p>
          You are responsible for the activity on your account and for keeping
          your credentials secure. Do not share your account. Notify us at
          <code> support@mythicmatrix.app</code> if you suspect unauthorized access.
        </p>
      </LegalSection>

      <LegalSection title="3. The AI Narrator">
        <p>
          Mythicmatrix uses large language models to generate interactive
          narrative. AI output is fictional and sometimes unexpected or
          imperfect. It is not professional advice of any kind — medical, legal,
          psychological, financial, or otherwise. Do not rely on AI output for
          decisions that affect real-world health, safety, or finances.
        </p>
      </LegalSection>

      <LegalSection title="4. Acceptable use">
        <p>You agree not to:</p>
        <LegalList>
          <li>Publish custom worlds whose lore, prompts, or starting scenes depict sexual content involving minors, real-world terrorism, incitement to violence against identifiable people, or other content prohibited by law.</li>
          <li>Attempt to extract system prompts, reverse-engineer our models, scrape at volume, or interfere with other players' chronicles.</li>
          <li>Use the service to generate content you intend to misrepresent as human-authored for academic, legal, or commercial purposes without disclosure.</li>
          <li>Impersonate others, harass users on the Founders' Wall or community listings, or submit content that infringes someone else's intellectual property.</li>
        </LegalList>
        <p>
          We may remove any content and suspend any account that violates these
          rules, with or without notice depending on severity.
        </p>
      </LegalSection>

      <LegalSection title="5. User-generated worlds">
        <p>
          When you publish a custom world you retain ownership of the lore and
          content you authored. You grant Mythicmatrix a non-exclusive,
          worldwide, royalty-free license to host, display, run, and allow
          other players to play that world through the service. You can
          unpublish a world at any time, but copies may remain in players'
          saved chronicles.
        </p>
      </LegalSection>

      <LegalSection title="6. Subscriptions, Founding Sovereign, and billing">
        <LegalList>
          <li>
            <strong className="text-stone-100">Free tier</strong> — every player
            can play every flagship world for up to 10 turns per adventure
            without paying. The 10-turn preview exists so you can try the
            experience before subscribing.
          </li>
          <li>
            <strong className="text-stone-100">Sovereign Monthly / Annual</strong> —
            recurring subscriptions that unlock unlimited turns, Hard Mode,
            and community worlds. Billed through Stripe. Renews automatically
            until you cancel in the billing portal. Cancellations take effect
            at the end of the current period.
          </li>
          <li>
            <strong className="text-stone-100">Sovereign Lifetime</strong> —
            one-time payment granting Sovereign access for the operational
            lifetime of the service.
          </li>
          <li>
            <strong className="text-stone-100">Founding Sovereign</strong> —
            limited one-time tier. Grants Lifetime plus a seat on the Founders'
            Wall. Non-transferable.
          </li>
          <li>
            Taxes may be added at checkout according to your region. Stripe
            handles tax calculation where required.
          </li>
        </LegalList>
      </LegalSection>

      <LegalSection title="7. Refunds">
        <p>
          Subscription payments are generally non-refundable, including for
          partial periods. We may issue a refund at our discretion if you
          contact <code>support@mythicmatrix.app</code> within 14 days of a charge
          and have not meaningfully used the service during that period.
          Statutory consumer protections in your jurisdiction (for example the
          EU 14-day right of withdrawal) continue to apply.
        </p>
      </LegalSection>

      <LegalSection title="8. The permadeath covenant">
        <p>
          If you play in Hard Mode and your character dies, the chronicle is
          sealed. It cannot be edited, undone, or revived — this is the design,
          not a bug. Dead chronicles remain in your vault as a record. We will
          not reverse a permadeath on request. By choosing Hard Mode you
          acknowledge and accept this.
        </p>
      </LegalSection>

      <LegalSection title="9. Advertising">
        <p>
          Free-tier chronicles display ads served by Google AdSense. See our
          <a href="/privacy" className="gold underline"> Privacy Policy</a> for
          details and opt-outs. Sovereigns do not see ads.
        </p>
      </LegalSection>

      <LegalSection title="10. Disclaimers and liability">
        <p>
          The service is provided "as is". We disclaim all implied warranties
          to the maximum extent permitted by law. To the maximum extent
          permitted by law, our aggregate liability to you for any claim arising
          from the service will not exceed the greater of (a) the amount you
          paid us in the twelve months preceding the claim or (b) USD $100.
          Nothing in these Terms limits liability that cannot be limited under
          applicable law (for example gross negligence or willful misconduct).
        </p>
      </LegalSection>

      <LegalSection title="11. Governing law and disputes">
        <p>
          These Terms are governed by the laws of the jurisdiction in which
          Mythicmatrix is operated. Disputes will first be addressed through
          good-faith correspondence to <code>support@mythicmatrix.app</code>. If a
          dispute cannot be resolved informally, it will be resolved by binding
          arbitration or in the courts of our operating jurisdiction, except
          where applicable consumer law requires otherwise.
          <em className="text-stone-500"> {" "}
            (Operator: update this section with your specific entity, governing
            state/country, and preferred dispute resolution venue before launch.)
          </em>
        </p>
      </LegalSection>

      <LegalSection title="12. Changes">
        <p>
          We may update these Terms. If a change is material, we will notify
          you by email or in-app notice before it takes effect. Continued use
          after the effective date constitutes acceptance.
        </p>
      </LegalSection>

      <LegalSection title="13. Contact">
        <p>
          Support: <code>support@mythicmatrix.app</code>. Privacy requests:
          <code> privacy@mythicmatrix.app</code>.
        </p>
      </LegalSection>
    </LegalShell>
  );
}
